import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";

import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { fetchNotifications } from "@/lib/queries";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — Quibblies" },
      {
        name: "description",
        content: "Follows, comments, moderation decisions and system updates for your account.",
      },
      { property: "og:title", content: "Notifications — Quibblies" },
      { property: "og:description", content: "Your Quibblies activity feed." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: NotificationsPage,
});

function NotificationsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => fetchNotifications(user!.id),
    enabled: Boolean(user),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", user!.id)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
    },
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });
    },
  });

  const rows = notifications.data ?? [];
  const unread = rows.filter((n) => !n.read_at).length;

  // Tip events (received, refunded, disputed, failed) are worth isolating —
  // they are the only notifications that concern money.
  const isTip = (n: (typeof rows)[number]) =>
    /\btip\b/i.test(`${n.title} ${n.body ?? ""}`);

  const [filter, setFilter] = React.useState<"all" | "unread" | "tips">("all");
  const visible = rows.filter((n) =>
    filter === "unread" ? !n.read_at : filter === "tips" ? isTip(n) : true,
  );

  const FILTERS = [
    { id: "all" as const, label: `All (${rows.length})` },
    { id: "unread" as const, label: `Unread (${unread})` },
    { id: "tips" as const, label: `Tips (${rows.filter(isTip).length})` },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-4xl text-foreground">Notifications</h1>
        <div className="flex items-center gap-2">
          {unread > 0 ? (
            <Button
              variant="soft"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              Mark all as read
            </Button>
          ) : null}
          <Button asChild variant="ghost">
            <Link to="/settings/notifications">Email settings</Link>
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filter notifications">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={filter === option.id}
            onClick={() => setFilter(option.id)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm transition-smooth",
              filter === option.id
                ? "border-primary/60 bg-primary/10 text-foreground"
                : "border-border/70 text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {notifications.isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))
        ) : visible.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="Nothing here"
            description="Follows, comments, tips and moderation updates will show up here."
          />
        ) : (
          visible.map((notification) => (
            <article
              key={notification.id}
              className={cn(
                "rounded-2xl border border-border/70 bg-gradient-card p-5",
                !notification.read_at && "border-primary/50",
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs uppercase tracking-wide text-primary-glow">
                  {isTip(notification) ? "tip" : notification.category}
                </span>
                <time className="text-xs text-muted-foreground" dateTime={notification.created_at}>
                  {new Date(notification.created_at).toLocaleString()}
                </time>
              </div>
              <h2 className="mt-2 font-display text-lg text-foreground">{notification.title}</h2>
              {notification.body ? (
                <p className="mt-1 text-sm text-muted-foreground">{notification.body}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                {notification.link ? (
                  <a
                    href={notification.link}
                    className="text-primary-glow underline-offset-4 hover:underline"
                  >
                    {notification.link.startsWith("/story/")
                      ? "View the publication"
                      : "Open details"}
                  </a>
                ) : null}
                {!notification.read_at ? (
                  <button
                    type="button"
                    onClick={() => markRead.mutate(notification.id)}
                    className="text-muted-foreground transition-smooth hover:text-foreground"
                  >
                    Mark as read
                  </button>
                ) : null}
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
